// Reduce una imagen del lado del cliente usando canvas, antes de subirla a las
// edge functions. Evita que el backend tenga que decodificar+resize en
// imagescript (que revienta el worker de Supabase con fotos >3MB del iPad).
//
// Uso:
//   const fileReducido = await reducirImagen(file, { maxLado: 1280, quality: 0.85 })
//
// Default: 1280px lado máximo, calidad 0.85 → fotos del iPad (4032×3024 ~ 5MB)
// quedan típicamente en 600-1000 KB, dentro del límite de Claude.

const DEFAULT_MAX_LADO = 1280
const DEFAULT_QUALITY = 0.85
// Si el archivo original ya es chico, no vale la pena re-encodear
const SKIP_SI_MENOR_QUE_BYTES = 500 * 1024  // 500 KB

export async function reducirImagen(file, opts = {}) {
  const maxLado = opts.maxLado || DEFAULT_MAX_LADO
  const quality = opts.quality || DEFAULT_QUALITY

  if (!file || !(file instanceof File)) return file
  if (file.size < SKIP_SI_MENOR_QUE_BYTES) return file

  // Crear bitmap desde el file
  const img = await crearImageBitmap(file)

  // Calcular nuevas dimensiones manteniendo aspect ratio
  let { width, height } = img
  if (width > maxLado || height > maxLado) {
    if (width >= height) {
      height = Math.round(height * (maxLado / width))
      width = maxLado
    } else {
      width = Math.round(width * (maxLado / height))
      height = maxLado
    }
  }

  // Dibujar en canvas
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0, width, height)

  // Liberar bitmap
  if (img.close) img.close()

  // Convertir a blob JPEG y devolverlo como File
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob falló'))),
      'image/jpeg',
      quality,
    )
  })

  // Devolver como File para preservar el nombre original
  const nuevoNombre = file.name.replace(/\.(jpg|jpeg|png|webp|heic|heif)$/i, '.jpg')
  return new File([blob], nuevoNombre, { type: 'image/jpeg', lastModified: Date.now() })
}

// Helper: crear ImageBitmap desde File (compatible con iOS Safari + HEIC limitado)
async function crearImageBitmap(file) {
  try {
    return await createImageBitmap(file)
  } catch (e) {
    // Fallback: usar HTMLImageElement (más lento pero compatible con HEIC en algunos browsers)
    const url = URL.createObjectURL(file)
    try {
      const imgEl = await new Promise((resolve, reject) => {
        const i = new Image()
        i.onload = () => resolve(i)
        i.onerror = () => reject(new Error('No se pudo cargar la imagen (formato no soportado)'))
        i.src = url
      })
      return imgEl
    } finally {
      URL.revokeObjectURL(url)
    }
  }
}

/** Reduce un array de Files en paralelo. */
export async function reducirImagenes(files, opts) {
  if (!Array.isArray(files) || files.length === 0) return []
  return Promise.all(files.map(f => reducirImagen(f, opts)))
}
